import React from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { linearGradientDef } from "@nivo/core";
import { ResponsiveLine } from "@nivo/line";
import { commaNotation } from "@utils";

const AREA_GRADIENT_ID = "hyro-line-chart-area-gradient";
const AREA_GRADIENT_BOTTOM = "#667EEA";

const tickLabelOf = (d) => d.label ?? d.x;

const collapseSparseData = (data) => {
  if (!data?.length || data.length <= 2) return data;
  const uniqueLabels = new Set(data.map(tickLabelOf));
  if (uniqueLabels.size >= 3) return data;
  return [data[0], data[data.length - 1]];
};

const pickEdgeAndMidTicks = (data) => {
  if (!data?.length) return [];
  const first = tickLabelOf(data[0]);
  const last = tickLabelOf(data[data.length - 1]);
  if (data.length === 1) return [first];
  const uniqueLabels = new Set(data.map(tickLabelOf));
  if (uniqueLabels.size < 3) return [first, last];
  if (data.length <= 3) return data.map(tickLabelOf);
  const mid = tickLabelOf(data[Math.floor(data.length / 2)]);
  return [first, mid, last];
};

const computeDefaultYScale = (data) => {
  if (!data?.length) return { type: "linear", min: "auto", max: "auto", stacked: false };
  const ys = data.map((d) => d.y);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const range = max - min || Math.abs(max) || 1;
  return {
    type: "linear",
    min: min - (range * 0.45),
    max,
    stacked: false
  };
};

const HyroLineChart = ({
  data = [],
  height = 80,
  loading = false,
  xTicks,
  gradient = true,
  gradientStops,
  interactive = true,
  showLine = true,
  yScale,
  formatValue = commaNotation
}) => {
  const theme = useTheme();

  const isFixedHeight = typeof height === "number";

  if (loading || !data.length) {
    return (
      <Box sx={{ height: isFixedHeight ? height : "100%", width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const renderedData = collapseSparseData(data);
  const series = [{ id: "value", data: renderedData }];
  const ticks = xTicks ?? pickEdgeAndMidTicks(renderedData);
  const effectiveYScale = yScale ?? computeDefaultYScale(renderedData);
  const baseline = typeof effectiveYScale.min === "number"
    ? effectiveYScale.min
    : Math.min(...renderedData.map((d) => d.y));
  const stops = gradientStops ?? [
    { offset: 5, color: theme.palette.secondary[400], opacity: 1 },
    { offset: 95, color: AREA_GRADIENT_BOTTOM, opacity: 0.1 }
  ];
  const layers = ["grid", "markers", "axes", "areas"];
  if (showLine) layers.push("lines");
  layers.push("crosshair", "points", "slices", "mesh", "legends");

  return (
    <Stack gap={.25} sx={{ width: "100%", height: isFixedHeight ? "auto" : height, minHeight: 0 }}>
      <Box sx={{ width: "100%", ...(isFixedHeight ? { height } : { flex: 1, minHeight: 0 }) }}>
        <ResponsiveLine
          data={series}
          margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
          xScale={{ type: "point" }}
          yScale={effectiveYScale}
          layers={layers}
          curve="catmullRom"
          colors={[theme.palette.secondary[500]]}
          lineWidth={1}
          enablePoints={true}
          pointSize={6}
          pointColor={"white"}
          pointBorderWidth={"1px"}
          pointBorderColor={theme.palette.secondary.main}
          enableArea
          areaOpacity={0.6}
          areaBaselineValue={baseline}
          defs={gradient ? [linearGradientDef(AREA_GRADIENT_ID, stops)] : []}
          fill={gradient ? [{ match: "*", id: AREA_GRADIENT_ID }] : []}
          enableGridX={false}
          enableGridY={false}
          axisLeft={null}
          axisRight={null}
          axisTop={null}
          axisBottom={null}
          isInteractive={interactive}
          enableCrosshair={false}
          enableTouchCrosshair={false}
          useMesh={interactive}
          tooltip={({ point }) => (
            <LineTooltip>
              <TooltipLabel>{point.data.label ?? point.data.xFormatted}</TooltipLabel>
              <TooltipValue>{formatValue(point.data.y)}</TooltipValue>
            </LineTooltip>
          )}
          animate
          motionConfig="gentle"
        />
      </Box>
      {ticks.length > 0 && (
        <TicksRow>
          {ticks.map((tick, i) => (
            <TickLabel key={`tick-${i}`}>{tick}</TickLabel>
          ))}
        </TicksRow>
      )}
    </Stack>
  );
};

const TicksRow = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%"
});

const TickLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[700],
  fontSize: 8,
  lineHeight: "normal",
  fontFamily: "inter,sans-serif"
}));

const LineTooltip = styled("div")(({ theme }) => ({
  background: theme.palette.system[0],
  padding: theme.spacing(1.5, 1.75),
  borderRadius: 4,
  boxShadow: "0px 3px 1px -2px rgba(59,83,105,0.2), 0px 2px 2px 0px rgba(59,83,105,0.14), 0px 1px 5px 0px rgba(59,83,105,0.12)",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
  whiteSpace: "nowrap",
  transform: "translateY(8px)",
  ...theme.typography.chip,

}));

const TooltipLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text[800]
}));

const TooltipValue = styled("span")(({ theme }) => ({
  color: theme.palette.text[700]
}));

export default HyroLineChart;
